import type { AuthResponse } from '@/interfaces/responses/auth';
import type { AxiosError } from 'axios';
import type { DataProcessingServiceConsent } from '@/libraries/usercentrics/types';
// import type { ErrorLike } from '@apollo/client';
// import type { GetUserSettingsQuery } from '@/features/user/graphql/queries/GetUserSettings.query';
import type { GetUserStatsQuery } from '@/graphql/queries/GetUserStats.query';
import type { Maybe } from '@/helpers/types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type {
	UserInfo,
	ProfileInfo,
	UserSearches,
	UserSettings,
	Consent,
	UpdateUserInfoPayload,
	UserExperimentsInfo,
	// UserJwConsents,
	UserUcConsents,
} from '../useUserStoreTypes';

import { GetUserStatsDocument } from '@/graphql/queries/GetUserStats.query';
import { ProfileSpecificLinkType, UserRole, PresentationType, MonetizationType } from '@/@types/graphql-types';

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
	BuyBoxFilterOptions,
	NewBuyBoxView,
	premiumPurchaseFlowState as PremiumPurchaseFlowState,
	EMAIL_HASH_KEY,
	CONSENT_PARTNERS,
} from '../useUserStoreTypes';
import { LoginProvider, LoginProviderByFirebaseProviderId } from '@/enums/login';
import { OnboardingStatus } from '@/stores/useUserStoreTypes';
import { WebLocale } from '@/enums/web-locale';
import {
	experimentConfig,
	ExperimentsCatalogue,
	isBackendExperiment,
	ValidExperimentName,
} from '@/libraries/experiment';
import { captureMessageForSentry, updateSentryTags } from '@/libraries/sentry';

import { LocalDataProcessingServices } from '@/helpers/usercentrics-helper';
import { DataProcessingService } from '@/libraries/usercentrics/types';
import { useLanguageStore } from './useLanguageStore';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { hasAnyConsentsGiven } from '@/helpers/consent-helper';
import { toUTCDateOnly } from '@/helpers/date-helper';
import { /*arraysAreEqual,*/ objectKeys } from '@/helpers/object-helper';
import { getIdServiceVariables } from '@/helpers/id-helper';
import {
	CancelProSubscriptionDocument,
	CancelProSubscriptionMutation,
	CancelProSubscriptionMutationVariables,
} from '@/components/modals/pro/graphql/mutations/CancelProSubscription.mutation';
import {
	GetNewDeviceIdInputDocument,
	GetNewDeviceIdInputMutation,
	GetNewDeviceIdInputMutationVariables,
} from '@/graphql/mutation/GetNewDeviceId.mutation';
import {
	SetUcConsentsDocument,
	SetUcConsentsMutation,
	SetUcConsentsMutationVariables,
} from '@/graphql/mutation/SetUCConsents.mutation';
import {
	RegisterLoginDocument,
	RegisterLoginMutation,
	RegisterLoginMutationVariables,
} from '@/graphql/mutation/RegisterLogin.mutation';
import {
	RegisterLogoutDocument,
	RegisterLogoutMutation,
	RegisterLogoutMutationVariables,
} from '@/graphql/mutation/RegisterLogout.mutation';
import {
	RegisterNewUserDocument,
	RegisterNewUserMutation,
	RegisterNewUserMutationVariables,
} from '@/graphql/mutation/RegisterNewUser.mutation';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { currentUser, generateToken } from '@/helpers/user-helper';
import { deleteUser, getAuth, sendEmailVerification, signOut } from 'firebase/auth';
import { getAxios } from '@/helpers/axios-helper';
import { generateHashedEmail } from '@/helpers/uuid-helper';
import { saveConsents as saveConsentsHelper } from '@/helpers/cookiebot-helper';
import { cookieStorage, STORE_NAMESPACE } from '@/stores/cookieStorage';
import { storeTCString } from '@/helpers/impression-tracker-helper';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useMounted, until } from '@vueuse/core';
// import { useSponsoredRecommendationsStore } from '@/stores/pinia/useSponsoredRecommendationsStore';
import { withRetry } from '@/helpers/retry-helper';
import {
	GetProfileByUrlDocument,
	GetProfileByUrlQuery,
	GetProfileByUrlQueryVariables,
} from '@/features/user-profile/graphql/queries/GetUserProfileByUrl.query';

import { supportsFreemium } from '@/features/jw-pro/helpers/supportsFreemium';
import { updatePremiumStatus } from '@/features/jw-pro/helpers/updatePremiumStatus';
// import { GetUserSettingsDocument } from '@/features/user/graphql/queries/GetUserSettings.query';
// import {
// 	UpdateUserSettingsDocument,
// 	UpdateUserSettingsMutation,
// 	UpdateUserSettingsMutationVariables,
// } from '@/features/user/graphql/mutations/UpdateUserSettings.mutation';

/** The UI lets you filter for Presentation type (HD, SD, ...) and also Monetization type Free */
export type BuyboxPresentationType = PresentationType | MonetizationType.Free;

function removeEmailHash() {
	if (!import.meta.client) return;

	localStorage.removeItem(EMAIL_HASH_KEY);
}

// store hashed email for snowplow flex-context event on title pages
async function saveEmailHash(email: string) {
	if (!import.meta.client) return;

	const hash = await generateHashedEmail(email);
	localStorage.setItem(EMAIL_HASH_KEY, JSON.stringify(hash));
}

function getDefaultSettings() {
	return {
		providers: [],
		ucConsents: null,
		jwConsents: (Object.keys(CONSENT_PARTNERS) as Consent[]).reduce((root, key) => {
			root[key] = null;
			return root;
		}, {} as Record<Consent, boolean | null>),
	};
}

// function omitTypename<T extends { __typename?: string }>({ __typename, ...rest }: T) {
// 	return rest;
// }

const RECENT_SEARCH_TERMS_COUNT = 10;

export const useUserStore = defineStore(
	'user',
	() => {
		const apollo = useApolloClient();

		const { jwConfig } = useRuntimeConfig().public;

		const languageStore = useLanguageStore();

		const hasLoadedSettings = ref(false);

		//State
		const accessToken = ref<string | null>(null); // used to send for certain routes with authorization token

		const enableProvidersFilter = ref<boolean>(false);
		const hasCancelledPremium = ref<boolean>(false);
		const hideSeenDislikedTitles = ref<boolean>(false);
		const info = ref<UserInfo>({});
		const profileIsComplete = ref<boolean>(false);
		const isVerificationEmailSent = ref<boolean>(false);
		const jwId = ref<string | null>(null);
		const jwLoginId = ref<string | null>(null);
		const lastUsedLoginProvider = ref<string | undefined>(undefined);
		const loggedInProviders = ref<LoginProvider[]>([]); // list of provider names the user is authenticated with
		const loginProgress = ref<boolean>(false);
		const persistedOverriddenLanguage = ref<string | null>(null);
		const persistedWebLocale = ref<WebLocale | null>(null);
		const permanentAudiencesSubgenres = ref<string[]>([]);

		const experimentsLoaded = ref(false);
		const preferredExperiments = ref<{ [experiment: string]: { variant: string; sendSnowplowEvent: boolean } }>({});
		const preferences = ref<{ buyboxPresentationType: BuyboxPresentationType | null }>({
			buyboxPresentationType: null,
		});

		const newBuyboxFilterType = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);
		const buyboxFilterTypeOptimistic = ref<BuyBoxFilterOptions | BuyboxPresentationType | null>(null);

		function setBuyboxFilterTypeOptimistic(
			filterType: null | BuyBoxFilterOptions | PresentationType | MonetizationType.Free
		) {
			buyboxFilterTypeOptimistic.value = filterType;
		}

		const newBuyboxView = ref<NewBuyBoxView>(NewBuyBoxView.LIST);
		const newBuyboxTooltipAcknowledged = ref<boolean>(false);
		// Temp: until we fully remove the old BB from the titlePage.
		const loggedInNewBuyBoxViewRef = ref<NewBuyBoxView | null>(null);
		const displayHomeSportsModule = ref<boolean>(true);
		const premiumPurchaseFlowState = ref<PremiumPurchaseFlowState | null>(null);
		const searches = ref<UserSearches>({ recentSearchTerms: [], recentlyVisitedTitleIds: [] });
		const seenSurveys = ref<Record<string, string[]>>({});

		const lastTimeMobileAppBannerWasClosedTS = ref<number>(0); // use to determine when mobile app banner was closed last time
		const lastTimeListsOnboardingWasShownTS = ref<number>(0); // use to determine when onboarding was shown last time
		// Start COOKIE_BANNER_EXPERIMENT
		const timeZone = ref<string | undefined>(undefined);

		// we need a way to reactively store all the DPS (data processing services) from Usercentrics.
		// then we can build an eligible DPS list by combining Usercentrics DPS and our usercentrics-helper.ts DPS.
		// with this we won't break any web app releases by changing them.
		const usercentricsDPS = ref<{ id: string; name: string }[] | null>(null);
		const userRole = ref<UserRole>(UserRole.User);

		const isUserFirstVisit = ref<boolean | null>(true); // true: default value, null: on first visit, false: user has visited before
		const showOldTitlePageState = ref<boolean>(false); // used to determine if we should show the old title page or not
		// contains all the experiments that the user was naturally acquired to, we get this from the BE.
		const acquiredExperiments = ref<ExperimentsCatalogue>({});
		// contains all the preferred experiments that were set by the user using query params (?preferred_variant=[experiment-name]:[experiment-variant])
		const _preferredExperiments = ref<ExperimentsCatalogue>({});
		// contains all the experiments that were activated for the user ["experimentName:variantName"]
		const activatedExperiments = ref<string[]>([]);
		// stores the TCF string from UserCentrics
		const uc_tcf = ref<string | null>(null);

		const accountCreationTime = ref<Date | null>(null); // used to be set in the user cookie instead of the whole info object.
		// used to determine if the user has opted out of consents
		const optedOutConsent = ref(false);

		// OLD BUYBOX EXP
		const buyboxExpView = ref(NewBuyBoxView.GRID);

		// only changes when settings.providers really have changed
		const settingsProvider = ref<string[]>([]);

		const settingsUCConsents = ref<UserSettings['ucConsents'] | null>(null);

		const settingsJWConsents = ref<UserSettings['jwConsents']>(getDefaultSettings().jwConsents);

		// EXP_PROVIDER_BUYBOX_V2
		const seenMyServices = ref(false);
		// EXP_PROVIDER_BUYBOX_V2

		const ageGateApproved = ref<boolean>(false);

		function setBuyboxExpView(viewType: NewBuyBoxView) {
			buyboxExpView.value = viewType;
			// Update Sentry tag
			updateSentryTags({ buybox_exp_view: viewType });
		}

		const buyboxViewExpTooltipAcknowledged = ref(false);
		function setBuyboxViewExpTooltipAcknowledged() {
			buyboxViewExpTooltipAcknowledged.value = true;
		}
		// OLD BUYBOX EXP

		//Getters
		const userInfo = computed(() => {
			return !accessToken.value ? {} : info.value;
		});
		const buyboxPresentationType = computed(() => {
			return preferences.value?.buyboxPresentationType ?? null;
		});

		const isLoggedIn = computed(() => (loggedInProviders.value?.length ?? []) > 0);

		const newBuyboxTooltipAcknowledgedGetter = computed(() => {
			return newBuyboxTooltipAcknowledged.value;
		});

		const bestEffortId = computed(() => jwLoginId.value || jwId.value);

		const isAccountVerified = computed(() => {
			const isAuthenticatedWithEmail = userInfo?.value.loginProvider === LoginProvider.JUSTWATCH;
			return isLoggedIn.value && (isAuthenticatedWithEmail ? !!info.value?.emailVerified : true);
		});

		const dpsConsents = computed(() => {
			const explicitlyConsented = (settingsUCConsents.value || [])
				.filter(item => item.consent)
				.map(item => item.id);
			const excplicitlyRejected = (settingsUCConsents.value || [])
				.filter(item => !item.consent)
				.map(item => item.id);

			return (Object.keys(LocalDataProcessingServices) as DataProcessingService[]).reduce((root, serviceKey) => {
				const service = LocalDataProcessingServices[serviceKey];
				let consentValue = null;
				if (explicitlyConsented.includes(service.id)) {
					// user HAS consented to this service
					consentValue = true;
				} else if (excplicitlyRejected.includes(service.id)) {
					// user HAS rejected consent to this service
					consentValue = false;
				} else {
					// user HAS NOT consented nor rejected to this service
					consentValue = null;
				}
				root![serviceKey] = consentValue;
				return root;
			}, {} as Record<DataProcessingService, boolean | null> | null);
		});

		// In cases like 'injecting external trackers' we need to have the
		// user's full consent to ALL partners that we currently have set up as DPS.
		// So we can be as data privacy compliant as possible.
		//
		// We differentiate between two groups:
		// - UC countries: all partner consents must have `consent: true`
		// - non-UC countries: the `jwConsents.allother` value must be `true`
		const strictlyAllConsentsGranted = computed(() => {
			if (hasUCConsentBanner(languageStore.webLocale)) {
				const ucConsents = settingsUCConsents.value;
				const totalDPS = usercentricsDPS.value ? usercentricsDPS.value.length : 0;
				// totalDPS is synced with UserCentrics, so it will be 0 in the beginning.
				// make sure they are loaded and not empty at 0.
				return totalDPS > 0 && usercentricsDPS.value?.length === ucConsents?.length;
			} else {
				return hasAnyConsentsGiven(settingsJWConsents.value);
			}
		});

		const userSearches = computed(() => {
			return {
				recentSearchTerms: (searches.value?.recentSearchTerms ?? []).slice(0, 10),
				recentlyVisitedTitleIds: (searches.value?.recentlyVisitedTitleIds ?? []).slice(0, 10),
			};
		});

		const isHideSeenDislikedTitlesEnabled = computed(() => {
			return hideSeenDislikedTitles.value;
		});
		const isAdmin = computed(() => {
			return userRole.value === UserRole.Admin;
		});
		const isEditor = computed(() => {
			return userRole.value === UserRole.Editor;
		});

		// Pro Status
		const isPremium = ref<boolean>(false);

		const isProUser = computed(() => userRole.value === UserRole.Pro || isPremium.value);

		const showFreemium = computed(() => supportsFreemium(isPremium.value, languageStore.webLocale));

		function setPremiumStatus(value: boolean) {
			const prevPremiumState = isPremium.value;
			isPremium.value = value;

			if (!value && prevPremiumState) {
				setHideSeenDislikedTitles(false);
				useFilterStore().resetPremiumFilters();
			}
		}

		async function updateUserPremiumStatus() {
			const { newPremiumStatus } = await updatePremiumStatus(jwLoginId.value);

			setPremiumStatus(newPremiumStatus);
		}

		// TITLE_DETAIL_SHAOW_PAGE_LOGGED_IN_EXP
		const loggedInNewBuyboxView = computed(() => {
			if (!isLoggedIn.value) {
				return newBuyboxView.value;
			}

			// ADDED THIS FOR COMPATIBILITY WITH OLD USER COOKIES (as they don't have the newly created accountCreationTime)
			const creationTime = accountCreationTime.value ?? info.value?.creationTime;
			const defaultView = languageStore.webLocale === WebLocale.uk ? NewBuyBoxView.LIST : NewBuyBoxView.GRID;
			// not great to use isMounted here but comments point to this logic being temporary...
			// account creation time is not available until `loadInfo` is called, but in the server, the current user is null
			// this leads to a mismatch for some users causing a hydration error on detail pages with buyboxes
			const isMounted = useMounted();

			// this one might be temporary but we wanna only show by default the new buybox view to users that signed up after a 2025-05-20
			if (isMounted.value && loggedInNewBuyBoxViewRef.value == null && creationTime) {
				// converting dates to UCT to prevent any hydration errors.
				const creationDate = toUTCDateOnly(new Date(creationTime));
				const comparisonDate = new Date(Date.UTC(2025, 4, 20));
				if (creationDate >= comparisonDate) {
					return NewBuyBoxView.LIST;
				}

				return defaultView;
			}

			return loggedInNewBuyBoxViewRef.value ?? defaultView;
		});

		// TITLE_DETAIL_SHAOW_PAGE_LOGGED_IN_EXP
		const showOldTitlePage = computed(() => {
			if (!isLoggedIn.value) {
				return false;
			}
			return showOldTitlePageState.value;
		});

		const userExperimentsInfo = computed<UserExperimentsInfo>(() => {
			const activeVariants: UserExperimentsInfo = {};
			for (const experimentName of objectKeys(acquiredExperiments.value ?? {})) {
				const activeVariant =
					experimentName in _preferredExperiments.value
						? _preferredExperiments.value[experimentName]
						: acquiredExperiments.value[experimentName];

				if (activeVariant) {
					activeVariants[experimentName] = {
						variant: activeVariant,
						preferred: experimentName in _preferredExperiments.value,
						activated:
							activatedExperiments.value.includes(experimentName + ':' + activeVariant) ||
							isBackendExperiment(experimentName),
						affiliationShortId:
							experimentConfig?.[experimentName as ValidExperimentName]?.affiliationShortId || null,
					};
				}
			}
			return activeVariants;
		});

		const tcString = computed(() => uc_tcf.value);

		// USER STATS
		const didFetchStats = ref(false);
		const knowsAboutTitleLists = ref(false);
		const maxCustomListsPro = ref(100);
		const maxCustomListsDefault = ref(2);

		const permanentAudiences = ref<string[]>([]);

		const customListsLimit = computed(() =>
			isPremium.value ? maxCustomListsPro.value : maxCustomListsDefault.value
		);

		async function fetchUserStats(refresh = false) {
			if (jwId.value == null) return;
			if (didFetchStats.value && !refresh) return;

			try {
				const { data, error } = await apollo.query<GetUserStatsQuery>({
					query: GetUserStatsDocument,
					errorPolicy: 'all',
				});

				if (error != null) {
					throw error;
				}

				setPremiumStatus(data?.stats.hasJWProSubscription ?? false);
				if (data?.stats.userRole) setUserRole(data.stats.userRole);

				knowsAboutTitleLists.value = data?.stats.knowsAboutTitleLists ?? false;
				maxCustomListsPro.value = data?.stats.maxCustomListsPro ?? 100;
				maxCustomListsDefault.value = data?.stats.maxCustomListsDefault ?? 2;
				permanentAudiences.value = data?.stats.permanentAudiences ?? [];
				ageGateApproved.value = data?.stats.userSettings.ageGateApproved ?? false;

				didFetchStats.value = true;
			} catch (error) {
				captureMessageForSentry(
					'Error fetching user stats.',
					{ error: error as Error, where: '[useUserStore.ts]: fetchUserStats' },
					'error'
				);
				didFetchStats.value = false;
			}
		}

		function resetUserStats() {
			knowsAboutTitleLists.value = false;
			maxCustomListsPro.value = 100;
			maxCustomListsDefault.value = 2;
			permanentAudiences.value = [];
			setPremiumStatus(false);
			setUserRole(UserRole.User);

			didFetchStats.value = false;
		}

		// USER PROFILE
		const profileInfo = ref<ProfileInfo | null>(null);

		async function fetchUserProfile() {
			if (!isLoggedIn.value) return;

			try {
				const { data, error } = await apollo.query<GetProfileByUrlQuery, GetProfileByUrlQueryVariables>({
					query: GetProfileByUrlDocument,
					variables: {
						country: languageStore.country,
						language: languageStore.language,
						firstArticles: 20,
						firstWtw: 10,
					},
				});

				if (error != null) {
					throw error;
				}

				const profile = data?.profile;
				if (profile == null) return;

				setProfileInfo({
					uuid: profile.id,
					firstName: profile.firstName,
					lastName: profile.lastName,
					city: profile.location ?? null,
					country: profile.country,
					bio: profile.bio ?? null,
					avatarUrl: profile.avatarUrl ?? null,
					website: profile.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Website),
					altWebsite: profile.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Other),
					instagram: profile.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Instagram),
					imdb: profile.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Imdb),
					x: profile.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Twitter),
					tiktok: profile.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Tiktok),
					linkedin: profile.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Linkedin),
					facebook: profile.externalUrls?.find(item => item.type === ProfileSpecificLinkType.Facebook),
				});

				setProfileIsComplete(profile.isComplete);
			} catch (error) {
				captureMessageForSentry(
					'Error fetching user profile.',
					{ error: error as Error, where: '[useUserStore.ts]: fetchUserProfile' },
					'error'
				);
			}
		}

		function setProfileInfo(info: ProfileInfo | null) {
			profileInfo.value = info;
		}

		// ACTIONS
		function setAcquiredExperiments(experiments: ExperimentsCatalogue) {
			acquiredExperiments.value = experiments;
		}

		function setNewPreferredExperiments(preferredExperiments: ExperimentsCatalogue) {
			_preferredExperiments.value = preferredExperiments;
		}

		function setActivatedExperiments(experiments: string[]) {
			activatedExperiments.value = experiments;
		}

		function registerActivatedExperiment(experimentName: string) {
			const experimentVariant = userExperimentsInfo.value?.[experimentName]?.variant;
			const activeExperiment = experimentName + ':' + experimentVariant;

			if (experimentVariant && experimentName && !activatedExperiments.value.includes(activeExperiment)) {
				addActivatedExperiment(activeExperiment);
			}
		}

		function addActivatedExperiment(experimentToActivate: string) {
			if (!activatedExperiments.value.includes(experimentToActivate)) {
				activatedExperiments.value.push(experimentToActivate);
			}
		}

		async function fetchJwId(source?: string) {
			try {
				const variables = await getIdServiceVariables(jwId.value || null, settingsJWConsents.value || {});

				const results = await apollo.mutate<GetNewDeviceIdInputMutation, GetNewDeviceIdInputMutationVariables>({
					mutation: GetNewDeviceIdInputDocument,
					variables: {
						input: {
							doNotTrack: variables.dnt,
							ids: [variables.ids],
						},
					},
				});

				if (results?.data?.getNewDeviceId.deviceId) {
					// fetching the JW ID is only for SEO users, no point in a request to reload default settings
					await setJwId(results?.data?.getNewDeviceId.deviceId, source ?? 'fetchJwId', false);
				} else {
					TrackingHelper.trackEvent('idservice', {
						action: 'lookup',
						label: 'failed',
						nonInteraction: true,
					});
					throw 'no valid ID received';
				}
			} catch (error) {
				// Webapp can init, but better keep tracking as this is still a limited app experience (no settings)
				captureMessageForSentry(
					'User Init Issue:',
					{ error: error as Error, where: '[user.store.ts] fetchJwId' },
					'error'
				);
			}
		}

		async function setJwId(id: string, source: string, personalize = true) {
			const oldId = jwId.value;
			jwId.value = id;

			if (personalize && oldId !== id) await loadSettings(source);
		}

		async function setJwLoginId(id: Maybe<string>, source: string) {
			const oldId = jwLoginId.value;
			jwLoginId.value = id ?? null;

			if (oldId != id) await loadSettings(source);
		}

		function setBuyboxPresentationType(payload: {
			presentationType: BuyboxPresentationType | null;
			additionalContexts?: SnowplowContext[];
		}) {
			const { presentationType, additionalContexts = [] } = payload;

			preferences.value.buyboxPresentationType = presentationType;
			TrackingHelper.trackEvent(
				'presentationtype_change',
				{ action: presentationType ?? undefined },
				additionalContexts
			);

			// Update Sentry tag
			updateSentryTags({
				buybox_presentation_type: presentationType === null ? 'best' : presentationType,
			});
		}

		function setNewBuyboxFilterType(filterType: BuyBoxFilterOptions) {
			newBuyboxFilterType.value = filterType;

			// Update Sentry tag
			updateSentryTags({ new_buybox_filter_type: filterType });
			// TrackingHelper.trackEvent('presentationtype_change', { action: filterType ?? undefined }, additionalContexts);
			// fail-safe that the optimistic value is not out of sync
			setBuyboxFilterTypeOptimistic(filterType);
		}

		function setNewBuyboxView(viewType: NewBuyBoxView) {
			if (isLoggedIn.value) {
				loggedInNewBuyBoxViewRef.value = viewType;
			}
			newBuyboxView.value = viewType;
			// Update Sentry tag
			updateSentryTags({ buybox_view: viewType });
		}

		function setNewBuyboxTooltipAcknowledged() {
			newBuyboxTooltipAcknowledged.value = true;

			// Update Sentry tag
			updateSentryTags({ buybox_tooltip_acknowledged: true });
		}

		function hideHomeSportsModule() {
			displayHomeSportsModule.value = false;
		}

		function setProfileIsComplete(isComplete: boolean) {
			profileIsComplete.value = isComplete;
		}

		/**
		 * Finds out if is login or signup
		 *
		 * @throws Error when user is not signed up yet
		 */
		async function verifyRegistration() {
			const user = await currentUser();

			if (!user) return;
			const tokenResult = await user.getIdTokenResult();

			if (tokenResult.claims.registration_error)
				throw `registration error: ${tokenResult.claims.registration_error}`;
			if (!tokenResult.claims.jw_login_id) throw 'not yet registered internally';
		}

		async function refreshToken() {
			let allRetriesFailed = false;
			let finalError: Error | undefined;

			const maxAttempts = 3;

			const token = await withRetry(
				async () => {
					const t = await generateToken(accessToken.value, jwId.value);
					if (t == null) throw new Error('Error getting refresh token');

					accessToken.value = t;

					return t;
				},

				async ({ attempt, maxAttempts, error }) => {
					captureMessageForSentry(
						`[useUserStore.ts] refreshToken attempt ${attempt}/${maxAttempts} failed`,
						{ error, where: 'useUserStore refreshToken' },
						attempt >= maxAttempts ? 'error' : 'warning'
					);

					if (attempt >= maxAttempts) {
						allRetriesFailed = true;
						finalError = error;
					}
				},

				{ maxAttempts }
			);

			if (allRetriesFailed) {
				// Only keep user logged in if jwLoginId exists AND this is likely a
				// transient Firebase issue (not an actually invalid token)
				if (jwLoginId.value) {
					captureMessageForSentry(
						'[useUserStore.ts] refreshToken failed after retries but jwLoginId exists - logging out to prevent broken state',
						{
							error: finalError,
							jwLoginId: jwLoginId.value,
							attempts: maxAttempts,
							where: 'useUserStore refreshToken',
						},
						'error'
					);
				}

				// Always logout after retries fail to prevent broken state
				// Even if jwLoginId exists, an invalid token means user can't make authenticated requests
				try {
					await logout();
					return null;
				} catch (error) {
					captureMessageForSentry(
						'[user.store.ts] refreshToken: Error logging out after failed retries',
						{ error: error as Error, where: 'user.store.ts refreshToken' },
						'error'
					);
				}
			}

			return token;
		}

		/** Logout for all providers. */
		async function logout(trackLogout = false) {
			// Call registerLogout to register in the backend that the user has signed out
			if (trackLogout) {
				if (accessToken.value && jwId.value) {
					// withAccessToken (src/graphql/utils/links.ts) already attaches
					// `Authorization: Bearer ${accessToken.value}` since it's still set here.
					await apollo.mutate<RegisterLogoutMutation, RegisterLogoutMutationVariables>({
						mutation: RegisterLogoutDocument,
					});
				} else {
					console.warn('Trying to logout without jwId');
					return;
				}
			}

			try {
				await signOut(getAuth());
			} catch (err) {
				console.error('[Firebase] Signout error: ', err);
			}

			accessToken.value = null;
			await setJwLoginId(null, 'logout');

			info.value = {};
			const lastLoginProvider: string = loggedInProviders.value[0] || 'justwatch';
			lastUsedLoginProvider.value = lastLoginProvider;
			setLoginProviders([]);

			resetUserStats();

			setPremiumStatus(false);
			setProfileInfo(null);
			setProfileIsComplete(false);

			removeEmailHash();
			removeTCString();

			// no-op as signed in users are already marked onboarded
			// even when signing out they stay onboarded (they see the module homepage)
			markUserOnboarded();

			const constantStore = useConstantStore();
			await saveSettings({ providers: constantStore.preselectedProviders });

			// useSponsoredRecommendationsStore().clearAllSponsoredAds();

			setLoginProgress(false);
		}

		async function saveSettings(partialSettings: Partial<UserSettings> = {}) {
			const locale = languageStore.locale;
			const localSettings = await mutateSettings(
				jwId.value,
				accessToken.value,
				locale,
				{
					providers: settingsProvider.value,
					jwConsents: settingsJWConsents.value,
					ucConsents: settingsUCConsents.value,
				},
				partialSettings
			);
			setSettings(localSettings);
		}

		async function loadSettings(source: string) {
			await until(jwId).toBeTruthy({ timeout: 500 });
			if (!jwId.value) return false;

			const settings = await getSettings(jwId.value, languageStore.locale, accessToken.value, source);

			setSettings(settings);
			hasLoadedSettings.value = true;
		}

		function setSettings(userSettings: UserSettings) {
			// make sure we only update setting's providers if they change
			const provider = userSettings.providers || [];
			if (provider && JSON.stringify(settingsProvider.value) !== JSON.stringify(provider)) {
				settingsProvider.value = provider;
			}

			settingsJWConsents.value = userSettings.jwConsents || {};
			settingsUCConsents.value = userSettings.ucConsents || [];
		}

		// async function saveSettingsGraphql({ jwConsents, ucConsents, providers }: Partial<UserSettings> = {}) {
		// 	const currentPackages = new Set(settingsProvider.value);
		// 	// If no change, keep current packages to avoid clearing them all with `Set.difference`
		// 	const changedPackages = new Set(providers?.length ? providers : settingsProvider.value);
		//
		// 	await apollo.mutate<UpdateUserSettingsMutation, UpdateUserSettingsMutationVariables>({
		// 		mutation: UpdateUserSettingsDocument,
		// 		variables: {
		// 			country: languageStore.country,
		// 			jwConsents,
		// 			ucConsents,
		// 			addPackageShortNames: [...changedPackages.difference(currentPackages)],
		// 			removePackageShortNames: [...currentPackages.difference(changedPackages)],
		// 		},
		// 	});
		//
		// 	// Settings mutations returns added/removed packages separately.
		// 	// Simpler to refetch the latest than combine them on the client.
		// 	await loadSettings('[useUserStore]: saveSettings');
		// }

		// async function loadSettingsGraphql(source: string) {
		// 	try {
		// 		if (!bestEffortId.value) return;
		//
		// 		const { data, error } = await apollo.query<GetUserSettingsQuery>({
		// 			query: GetUserSettingsDocument,
		// 			variables: { country: languageStore.country },
		// 			// no-cache because the variables don't change meaning the query results get cached
		// 			fetchPolicy: 'no-cache',
		// 		});
		//
		// 		if (error != null) throw error;
		//
		// 		const userSettings = data?.settings;
		// 		if (userSettings == null) throw new Error('No settings returned.');
		//
		// 		const providerShortNames = userSettings.providers.map(p => p.shortName);
		//
		// 		// only update setting's providers if they change
		// 		if (!arraysAreEqual(providerShortNames, settingsProvider.value)) {
		// 			settingsProvider.value = providerShortNames;
		// 		}
		//
		// 		settingsJWConsents.value = userSettings.jwConsents
		// 			? omitTypename(userSettings.jwConsents)
		// 			: getDefaultSettings().jwConsents;
		//
		// 		settingsUCConsents.value = userSettings.ucConsents.map(c => omitTypename(c));
		// 	} catch (error) {
		// 		console.error(error);
		// 		captureMessageForSentry(
		// 			'Error loading user settings.',
		// 			{ error: error as ErrorLike, where: '[useUserStore.ts]: loadSettings', source },
		// 			'error'
		// 		);
		//
		// 		const { providers, jwConsents, ucConsents } = getDefaultSettings();
		// 		settingsProvider.value = providers;
		// 		settingsJWConsents.value = jwConsents;
		// 		settingsUCConsents.value = ucConsents;
		// 	} finally {
		// 		hasLoadedSettings.value = true;
		// 	}
		// }

		function setOptedOutConsent(optedOut: boolean) {
			optedOutConsent.value = optedOut;
		}

		function setHideSeenDislikedTitles(value: boolean) {
			hideSeenDislikedTitles.value = value;
		}

		function removeTCString() {
			if (!import.meta.client) return;

			sessionStorage.removeItem('tcString');
		}

		// EXP_PROVIDER_BUYBOX_V2: guests can pick providers before signing up (buybox "My Services" flow).
		// Carry that selection through login instead of losing it. Safe to delete this whole function
		// (and its call sites in registerInternally/signInInternally) once the experiment concludes.
		function getGuestProvidersForExpCarryover(): string[] | null {
			const variant = userExperimentsInfo.value['EXP_PROVIDER_BUYBOX_V2']?.variant;
			if (!variant || variant === 'control') return null;
			return settingsProvider.value?.length ? settingsProvider.value : null;
		}

		async function registerInternally() {
			if (isLoggedIn.value) return;

			try {
				setLoginProgress(true);

				// EXP_PROVIDER_BUYBOX_V2
				const loggedOutProviders = getGuestProvidersForExpCarryover();
				// EXP_PROVIDER_BUYBOX_V2

				const user = await currentUser();

				if (!user) {
					throw new Error('[Firebase] internal registration failed: user not found');
				}

				// Get the firebase token, at this point we don't have the jw_login_id as part of claims
				const tokenResult = await user.getIdTokenResult(true);
				if (!tokenResult) {
					throw new Error('internal registration failed');
				}
				const { token } = tokenResult;

				await apollo.mutate<RegisterNewUserMutation, RegisterNewUserMutationVariables>({
					mutation: RegisterNewUserDocument,
					context: { headers: { Authorization: 'Bearer ' + token } },
				});

				// After registration, get the token result again, this should now contain the jw_login_id in claims.
				const newTokenResult = await user.getIdTokenResult(true);
				if (!newTokenResult || !newTokenResult.claims.jw_login_id) {
					throw new Error('internal registration failed');
				}

				if (user.email) {
					await sendEmailVerification(user, { url: location.toString() });
				}

				await setAuthData(
					{
						connected_accounts: user.providerData.map(p => ({
							email: p.email ?? undefined,
							providerId: p.providerId,
							login_provider: LoginProviderByFirebaseProviderId[p.providerId] as LoginProvider,
						})),
						connected_providers: [],
						device_id: jwId.value!,
						login_id: newTokenResult.claims.jw_login_id as string,
						token: newTokenResult.token,
						loginProvider: LoginProviderByFirebaseProviderId[
							tokenResult.signInProvider as string
						] as LoginProvider,
					},
					'registerInternally'
				);

				const constantStore = useConstantStore();

				// EXP_PROVIDER_BUYBOX_V2: prefer what the guest already picked over the generic default
				await saveSettings({
					providers: loggedOutProviders?.length ? loggedOutProviders : constantStore.preselectedProviders,
				});
				// EXP_PROVIDER_BUYBOX_V2
				await loadInfo();

				if (info.value?.email) await saveEmailHash(info.value.email);

				if (premiumPurchaseFlowState.value == PremiumPurchaseFlowState.AUTH_STARTED) {
					// set state to continue purchase flow without user interaction
					setPremiumPurchaseFlowState(PremiumPurchaseFlowState.AUTH_SUCCESS);
				}

				markUserOnboarded();

				// useSponsoredRecommendationsStore().clearAllSponsoredAds();
			} catch (err) {
				throw err;
			} finally {
				setLoginProgress(false);
			}
		}

		function setPremiumPurchaseFlowState(value: PremiumPurchaseFlowState | null) {
			premiumPurchaseFlowState.value = value;
		}

		async function loadInfo() {
			if (!accessToken.value) {
				info.value = {};
				accountCreationTime.value = null;
				return;
			}

			const user = await currentUser();

			const creationTime = user?.metadata.creationTime;
			accountCreationTime.value = creationTime ? new Date(creationTime) : null;

			info.value = {
				name: user?.displayName || '',
				email: user?.email || '',
				loginProvider: info.value.loginProvider,
				emailVerified: user?.emailVerified,
				creationTime: accountCreationTime.value ?? undefined,
				photoURL: user?.photoURL || '',
			};
		}

		async function setAuthData(authData: AuthResponse, source: string) {
			if (authData.connected_accounts) {
				const providers: LoginProvider[] = [];

				for (let i = 0; i < authData.connected_accounts.length; i++) {
					// @ts-expect-error properties below are marked as not existing because of missing types
					const { email, login_provider, is_entry_point } = authData.connected_accounts[i];

					if (is_entry_point) {
						info.value.email = email;

						info.value.loginProvider = login_provider;
					}
					providers.push(login_provider);
				}

				setLoginProviders(providers);
			} else {
				if (authData.connected_providers.length === 1) {
					info.value.loginProvider = authData.connected_providers[0];
				}

				setLoginProviders(authData.connected_providers);
			}

			info.value.loginProvider = authData.loginProvider;

			accessToken.value = authData.token;
			await setJwLoginId(authData.login_id, source);
		}

		function setLoginProgress(value: boolean) {
			if (!value) resetUserStats();

			loginProgress.value = value;
		}

		async function signInInternally() {
			try {
				setLoginProgress(true);

				// EXP_PROVIDER_BUYBOX_V2
				const loggedOutProviders = getGuestProvidersForExpCarryover();
				// EXP_PROVIDER_BUYBOX_V2

				const user = await currentUser();

				if (!user) {
					setLoginProgress(false);
					throw new Error('no firebase user');
				}

				const tokenResult = await user.getIdTokenResult(true);
				if (!tokenResult || !tokenResult.claims.jw_login_id) {
					throw new Error('need JW login ID for signing in internally');
				}

				await apollo.mutate<RegisterLoginMutation, RegisterLoginMutationVariables>({
					mutation: RegisterLoginDocument,
					context: { headers: { Authorization: 'Bearer ' + tokenResult.token } },
				});

				await setAuthData(
					{
						connected_accounts: user.providerData.map(p => ({
							email: p.email ?? undefined,
							providerId: p.providerId,
							login_provider: LoginProviderByFirebaseProviderId[p!.providerId] as LoginProvider,
						})),
						connected_providers: [],
						device_id: jwId.value!,
						login_id: tokenResult.claims.jw_login_id as string,
						token: tokenResult.token,
						loginProvider: LoginProviderByFirebaseProviderId[
							tokenResult.signInProvider as string
						] as LoginProvider,
					},
					'signInInternally'
				);

				// EXP_PROVIDER_BUYBOX_V2: this account has no providers of its own saved yet, so carry over
				// what the guest already picked instead of leaving My Services empty.
				if (loggedOutProviders?.length && !settingsProvider.value?.length) {
					await saveSettings({ providers: loggedOutProviders });
				}
				// EXP_PROVIDER_BUYBOX_V2

				await updateUserPremiumStatus();
				await loadInfo();

				if (info.value?.email) await saveEmailHash(info.value.email);

				// if sign-in/up was part of premium purchase flow
				if (premiumPurchaseFlowState.value == PremiumPurchaseFlowState.AUTH_STARTED) {
					if (isPremium.value) {
						// user already has Premium, abort purchase flow
						setPremiumPurchaseFlowState(null);
					} else {
						// set state to continue purchase flow without user interaction
						setPremiumPurchaseFlowState(PremiumPurchaseFlowState.AUTH_SUCCESS);
					}
				}

				markUserOnboarded();

				// useSponsoredRecommendationsStore().clearAllSponsoredAds();
			} catch (err) {
				throw err;
			} finally {
				setLoginProgress(false);
			}
		}

		async function sendCode(code: string) {
			if (accessToken.value) {
				return getAxios().post(
					`${jwConfig.USER_URL}/tv/connect`,
					{ code },
					{ headers: { 'X-Access-Token': accessToken.value } }
				);
			}
		}

		// @deprecated: use saveDPSConsents for newer consent management
		function saveConsents(consents: Record<Consent, boolean>) {
			saveSettings({ jwConsents: consents });
			TrackingHelper.trackConsent(consents);
			TrackingHelper.trackEvent('consent', {
				action: 'saved',
				nonInteraction: true,
			});
			saveConsentsHelper(consents);
			console.log('@todo replace with UsercentricsHelper.saveConsents(consents)');
		}

		/**
		 * we only locally save DPS consents that either:
		 * - have been answered explicitly
		 * - are true (usually being 'essential')
		 *
		 * note: it's possible that `usercentricsDpsConsents` has newer DPS that the local DPS (usercentrics-helper@LocalDataProcessingServices) don't have.
		 * in the getters `dpsConsents` we ignore DPS (either from UC or local) that aren't present in both sets.
		 */
		async function saveDPSConsents(usercentricsDpsConsents: DataProcessingServiceConsent[]) {
			const explicitConsents = usercentricsDpsConsents.filter(consent => consent.hasAnswered);
			const ucConsents = explicitConsents.map(item => ({ id: item.id, consent: item.status }));

			// we want to prevent saving the uc_consents if they haven't changed.
			const getStringifiedConsent = (consents: UserUcConsents[]) =>
				JSON.stringify([...(consents || [])].sort((a, b) => b.id.localeCompare(a.id, 'en')));

			if (getStringifiedConsent(settingsUCConsents.value ?? []) !== getStringifiedConsent(ucConsents)) {
				settingsUCConsents.value = ucConsents;
				syncUCConsentsToBackend(explicitConsents);
				// Store the GDPR Consent TC string if it has changed
				setTCString(await storeTCString());
			}
		}

		/**
		 * persists explicitly-answered UC consents via `setUCConsents` (works for logged-in or anonymous users).
		 * this REPLACES the full stored snapshot, so only pass explicit consents — never defaults.
		 * failures are only logged.
		 */
		async function syncUCConsentsToBackend(explicitDpsConsents: DataProcessingServiceConsent[]) {
			const consents = explicitDpsConsents
				.filter(item => item.id && item.name)
				.map(item => ({ id: item.id, name: item.name, granted: item.status }));

			if (consents.length === 0) return;

			try {
				const { error } = await apollo.mutate<SetUcConsentsMutation, SetUcConsentsMutationVariables>({
					mutation: SetUcConsentsDocument,
					variables: { input: { consents } },
					errorPolicy: 'all',
				});

				if (error != null) {
					throw error;
				}
			} catch (error) {
				captureMessageForSentry(
					'Error syncing UC consents.',
					{ error: error as Error, where: '[useUserStore.ts]: syncUCConsentsToBackend' },
					'error'
				);
			}
		}

		function setLoginProviders(providers: LoginProvider[]) {
			loggedInProviders.value = providers;
		}

		function updateUserInfo(userInfoPayload: UpdateUserInfoPayload) {
			info.value = {
				...info.value,
				...userInfoPayload,
			};
		}

		async function deleteAccountPermanently() {
			// needs to be before the first await for Vue instance context
			const { mutate: cancelProMutation } = useMutation<
				CancelProSubscriptionMutation,
				CancelProSubscriptionMutationVariables
			>(CancelProSubscriptionDocument);

			const user = await currentUser();
			if (!user) throw 'not logged in to Firebase';

			try {
				if (isPremium.value) {
					await cancelProMutation();
				}

				await deleteUser(user);
				await logout();
			} catch (e) {
				const err = e as Error & { code: string };
				if (err.code !== 'auth/requires-recent-login') {
					captureMessageForSentry(
						'Error deleting user account:',
						{ error: err, where: '[user.store.ts] deleteAccountPermanently' },
						'error'
					);
				}
				throw e;
			}
		}

		function savePersistedOverriddenLanguage(language: string) {
			persistedOverriddenLanguage.value = language;
		}

		function saveSearchTerm(searchTerm: string) {
			if (searchTerm.trim().length > 0) {
				if (!searches.value.recentSearchTerms.includes(searchTerm)) {
					// recentSearchTerms = [searchTerm, ...recentSearchTerms];
					searches.value.recentSearchTerms = [searchTerm].concat(searches.value.recentSearchTerms);
				} else {
					const index = searches.value.recentSearchTerms.findIndex(term => term === searchTerm);
					const term = searches.value.recentSearchTerms[index];
					searches.value.recentSearchTerms.splice(0, 0, term!);
					searches.value.recentSearchTerms.splice(index + 1, 1);
				}

				// limit stored search terms to 10 to avoid store explosion
				searches.value.recentSearchTerms = searches.value.recentSearchTerms.slice(0, RECENT_SEARCH_TERMS_COUNT);
			}
		}

		function saveVisitedTitleId(visitedTitleId: string) {
			const containsTitle: boolean =
				searches.value.recentlyVisitedTitleIds?.some((elem: string) => elem === visitedTitleId) ?? false;

			if (!containsTitle) {
				searches.value.recentlyVisitedTitleIds = [visitedTitleId].concat(
					searches.value.recentlyVisitedTitleIds
				);
			} else {
				const index = searches.value.recentlyVisitedTitleIds.findIndex(titleId => titleId === visitedTitleId);
				const title = searches.value.recentlyVisitedTitleIds[index];
				searches.value.recentlyVisitedTitleIds.splice(0, 0, title!);
				searches.value.recentlyVisitedTitleIds.splice(index + 1, 1);
			}

			// limit stored search terms to 10 to avoid store explosion
			searches.value.recentlyVisitedTitleIds = searches.value.recentlyVisitedTitleIds.slice(
				0,
				RECENT_SEARCH_TERMS_COUNT
			);
		}

		function clearSearchHistory() {
			searches.value.recentSearchTerms = [];
			searches.value.recentlyVisitedTitleIds = [];
		}

		function setPremiumCancellationStatus(value: boolean) {
			hasCancelledPremium.value = value;
		}

		function setTimeZone(value: string) {
			timeZone.value = value;
		}

		function setIsVerificationEmailSent(isSent: boolean) {
			isVerificationEmailSent.value = isSent;
		}

		function setLastTimeMobileAppBannerWasClosedTS(timeStamp: number) {
			lastTimeMobileAppBannerWasClosedTS.value = timeStamp;
		}

		function setLastTimeListOnboardingWasShownTS(timeStamp: number) {
			lastTimeListsOnboardingWasShownTS.value = timeStamp;
		}

		async function updateUserVisitStatus() {
			if (isUserFirstVisit.value === true) {
				isUserFirstVisit.value = null;
			} else if (isUserFirstVisit.value === null) {
				isUserFirstVisit.value = false;
			}
		}

		function setPermanentAudiencesSubgenres(audiences: string[]) {
			permanentAudiencesSubgenres.value = audiences;
			TrackingHelper.refreshPermanentAudiencesSubgenres(audiences);
		}

		function resetPermanentAudiencesSubgenres() {
			permanentAudiencesSubgenres.value = [];
		}

		function setUserRole(role: UserRole) {
			userRole.value = role;
		}

		function toggleShowOldTitlePage() {
			showOldTitlePageState.value = !showOldTitlePageState.value;
		}

		function setUsercentricsDPS(services: { id: string; name: string }[]) {
			usercentricsDPS.value = services;
		}

		function setPersistedWebLocale(webLocale: WebLocale) {
			persistedWebLocale.value = webLocale;
		}

		const onboardingStatus = ref<OnboardingStatus>(OnboardingStatus.GLOBAL);
		// This could just be a boolean but some user settings depend on the enum values
		const isUserOnboarded = computed(() => onboardingStatus.value === OnboardingStatus.ONBOARDED);

		// Once onboarded, a user stays onboarded
		// Clicks Home Page CTA, Logs In, Logs Out, even Deletes account
		function markUserOnboarded() {
			onboardingStatus.value = OnboardingStatus.ONBOARDED;
		}

		function setTCString(tcString: string | null) {
			if (tcString == null) return;
			uc_tcf.value = tcString;
		}

		// EXP_PROVIDER_BUYBOX_V2
		function setSeenMyServices() {
			seenMyServices.value = true;
		}
		// EXP_PROVIDER_BUYBOX_V2

		return {
			hasLoadedSettings,

			//#region state
			accessToken,
			accountCreationTime,
			enableProvidersFilter,
			hasCancelledPremium,
			hideSeenDislikedTitles,
			info,
			isPremium,
			profileIsComplete,
			isVerificationEmailSent,
			jwId,
			jwLoginId,
			lastUsedLoginProvider,
			loggedInProviders,
			loginProgress,
			onboardingStatus,
			persistedOverriddenLanguage,
			persistedWebLocale,
			permanentAudiencesSubgenres,
			experimentsLoaded,
			preferredExperiments,
			preferences,
			newBuyboxFilterType,
			buyboxFilterTypeOptimistic,
			newBuyboxView,
			newBuyboxTooltipAcknowledged,
			loggedInNewBuyBoxViewRef,
			displayHomeSportsModule,
			premiumPurchaseFlowState,
			searches,
			seenSurveys,
			settingsProvider,
			lastTimeMobileAppBannerWasClosedTS,
			lastTimeListsOnboardingWasShownTS,
			timeZone,
			usercentricsDPS,
			userRole,

			isUserFirstVisit,
			showOldTitlePageState,
			acquiredExperiments,
			_preferredExperiments,
			activatedExperiments,
			uc_tcf,
			optedOutConsent,
			// #endregion

			//#region getters

			userInfo,
			buyboxPresentationType,
			isLoggedIn,
			newBuyboxTooltipAcknowledgedGetter,
			bestEffortId,
			dpsConsents,
			strictlyAllConsentsGranted,
			userSearches,
			isUserOnboarded,
			isHideSeenDislikedTitlesEnabled,
			isAdmin,
			isEditor,
			isAccountVerified,
			isProUser,
			loggedInNewBuyboxView,
			showOldTitlePage,
			userExperimentsInfo,
			tcString,
			showFreemium,
			settingsJWConsents,
			settingsUCConsents,
			ageGateApproved,
			//#endregion

			//#region actions
			setAcquiredExperiments,
			setNewPreferredExperiments,
			setActivatedExperiments,
			registerActivatedExperiment,
			addActivatedExperiment,
			setJwId,
			fetchJwId,
			setBuyboxPresentationType,
			setNewBuyboxFilterType,
			setBuyboxFilterTypeOptimistic,
			setNewBuyboxView,
			setNewBuyboxTooltipAcknowledged,
			hideHomeSportsModule,
			verifyRegistration,
			setPremiumStatus,
			loadSettings,
			saveSettings,
			logout,
			refreshToken,
			setLoginProgress,
			setAuthData,
			loadInfo,
			registerInternally,
			signInInternally,
			sendCode,
			saveConsents,
			saveDPSConsents,
			setLoginProviders,
			updateUserInfo,
			deleteAccountPermanently,
			savePersistedOverriddenLanguage,
			saveSearchTerm,
			saveVisitedTitleId,
			clearSearchHistory,
			setPremiumCancellationStatus,
			setTimeZone,
			setIsVerificationEmailSent,
			setLastTimeMobileAppBannerWasClosedTS,
			setLastTimeListOnboardingWasShownTS,
			updateUserVisitStatus,
			setPermanentAudiencesSubgenres,
			resetPermanentAudiencesSubgenres,
			setUserRole,
			toggleShowOldTitlePage,
			setUsercentricsDPS,
			setPersistedWebLocale,
			setHideSeenDislikedTitles,
			markUserOnboarded,

			setTCString,
			setPremiumPurchaseFlowState,
			setOptedOutConsent,
			updateUserPremiumStatus,
			//#endregion

			// OLD BUYBOX EXP
			buyboxExpView,
			setBuyboxExpView,
			buyboxViewExpTooltipAcknowledged,
			setBuyboxViewExpTooltipAcknowledged,
			// OLD BUYBOX EXP

			// EXP_PROVIDER_BUYBOX_V2
			seenMyServices,
			setSeenMyServices,
			// EXP_PROVIDER_BUYBOX_V2

			// USER STATS
			knowsAboutTitleLists,
			maxCustomListsPro,
			maxCustomListsDefault,
			customListsLimit,
			permanentAudiences,
			didFetchStats,
			fetchUserStats,
			// END USER STATS

			// USER PROFILE
			profileInfo,
			setProfileInfo,
			setProfileIsComplete,
			fetchUserProfile,
			// END USER PROFILE
		};
	},
	{
		persist: import.meta.server
			? false
			: [
					{
						storage: {
							getItem() {
								return null;
							},
							setItem(key, value) {
								if (typeof localStorage === 'undefined') return;
								localStorage.setItem(`${STORE_NAMESPACE}/${key}`, value);
							},
						},
					},
					{
						storage: cookieStorage,
						pick: [
							'accessToken',
							'hideSeenDislikedTitles',
							'isPremium',
							'userRole',
							'jwId',
							'jwLoginId',
							'lastUsedLoginProvider',
							'loggedInProviders',
							'onboardingStatus',
							'persistedOverriddenLanguage',
							'persistedWebLocale',
							'preferences',
							'settingsProvider',
							'newBuyboxFilterType',
							'newBuyboxView',
							'newBuyboxTooltipAcknowledged',
							'loggedInNewBuyBoxViewRef',
							'loggedInNewBuyBoxView',
							'buyboxExpView',
							'accountCreationTime',
							'showOldTitlePageState',
						],
					},
			  ],
	}
);

async function getSettings(
	justwatch_id: string | null = '',
	locale = '',
	accessToken: string | null = '',
	source: string
): Promise<UserSettings> {
	// don't fetch personalized data when jwId is not available
	if (!justwatch_id) return Promise.resolve(getDefaultSettings());

	const defaultSettings = getDefaultSettings();

	try {
		const response = await getAxios().get(`${JW_CONFIG.WRITE_URL}/settings/${locale}`, {
			params: { justwatch_id },
			headers: { 'X-Access-Token': accessToken ?? '' },
		});

		const {
			jw_consents: jwConsents = defaultSettings.jwConsents,
			uc_consents: ucConsents = defaultSettings.ucConsents,
			providers = defaultSettings.providers,
		} = response.data;

		if (jwConsents?.terms_and_conditions != null) {
			jwConsents.termsAndConditions = jwConsents.terms_and_conditions;
		}

		return { jwConsents, ucConsents, providers } as UserSettings;
	} catch (error) {
		const status = (error as AxiosError).response?.status;
		captureMessageForSentry(
			`[getSettings()] - ${status}. Source: ${source}`,
			{ error: error as AxiosError, where: 'settings-helper.ts' },
			'error'
		);

		return Promise.resolve(defaultSettings);
	}
}

async function mutateSettings(
	justwatch_id: string | null = '',
	accessToken: string | null = '',
	locale = '',
	currentSettings: UserSettings,
	newSettings: Partial<UserSettings>
) {
	if (!justwatch_id) return Promise.resolve(currentSettings || getDefaultSettings());

	const defaultSettings = getDefaultSettings();

	try {
		const mutations = [];

		for (const [field, value] of Object.entries(newSettings)) {
			if (field === 'jwConsents') {
				const currentJwConsents = {
					...currentSettings.jwConsents,
					terms_and_conditions: currentSettings.jwConsents.termsAndConditions,
				};

				const newJwConsents = {
					...value,
					terms_and_conditions: (value as UserSettings['jwConsents'])?.termsAndConditions,
				};

				// @ts-expect-error BE might be super strict about allowed keys
				delete currentJwConsents?.termsAndConditions;
				// @ts-expect-error BE might be super strict about allowed keys
				delete newJwConsents?.termsAndConditions;

				mutations.push({ field: 'jw_consents', unset: currentJwConsents, set: newJwConsents });
			} else if (field === 'ucConsents') {
				mutations.push({ field: 'uc_consents', unset: currentSettings.ucConsents, set: value });
			} else if (field === 'providers') {
				mutations.push({ field: 'providers', unset: currentSettings.providers, set: value });
			}
		}

		const response = await getAxios().post(
			`${JW_CONFIG.WRITE_URL}/settings/${locale}/mutate`,
			{ mutations },
			{ params: { justwatch_id }, headers: { 'X-Access-Token': accessToken ?? '' } }
		);

		const {
			jw_consents: jwConsents = defaultSettings.jwConsents,
			uc_consents: ucConsents = defaultSettings.ucConsents,
			providers = defaultSettings.providers,
		} = response.data;

		return { jwConsents, ucConsents, providers } as UserSettings;
	} catch (error) {
		const status = (error as AxiosError).response?.status;
		captureMessageForSentry(
			`[mutateSettings()] - ${status}`,
			{ error: error as AxiosError, where: 'settings-helper.ts' },
			'error'
		);

		return Promise.resolve(currentSettings || defaultSettings);
	}
}
